import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Avatar,
  DataTable,
  EmptyState,
  Icon,
  PageBody,
  Pill,
  Select,
  SegmentedControl,
  StatTile,
  StatTileSkeleton,
  useToast,
  type SortState,
} from "@astrum/ui";
import { api } from "../lib/api.js";
import { ExportMenu } from "../components/ExportMenu.js";
import { toCsv, downloadCsv } from "../lib/csv.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./daily-report.css";

/**
 * A manager's rollup: everyone who reports to the viewer (everyone active,
 * for an admin), with a computed health status rather than a preview of
 * what they wrote — the actual report text is one click away on the person
 * page, so repeating it here just to truncate it again added nothing this
 * table's own job (who needs attention) couldn't already say faster.
 *
 * Defaults to only the people actually asked to check in. An admin's roster
 * is every active account in the workspace, most of which were never given
 * Check-in Required — showing all of them by default turned "who needs
 * attention" into "everyone who's never once reported," which is a
 * different, far less useful question. Everyone is still one filter away.
 */

interface TeamMember {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  checkInRequired: boolean;
  department: string | null;
  blockersThisWeek: number;
  lastReportedDate: string | null;
  currentStreak: number;
  reportsThisWeek: number;
  status: "missing" | "at-risk" | "on-track";
  report: {
    whatIDid: string;
    blockers: string | null;
    tomorrowsPlan: string | null;
  } | null;
}

type StatusFilter = "all" | "missing" | "at-risk" | "on-track";
type RosterFilter = "required" | "everyone";

const STATUS_LABEL: Record<TeamMember["status"], string> = {
  missing: "Missing",
  "at-risk": "At risk",
  "on-track": "On track",
};

const STATUS_TONE: Record<TeamMember["status"], "danger" | "warn" | "ok"> = {
  missing: "danger",
  "at-risk": "warn",
  "on-track": "ok",
};

/** The default sort order: missing first (the thing worth acting on), then
 *  at-risk, then everyone who's fine. */
const STATUS_RANK: Record<TeamMember["status"], number> = { missing: 0, "at-risk": 1, "on-track": 2 };

function formatLastReported(date: string | null): string {
  if (!date) return "Never";
  const [year, month, day] = date.split("-").map(Number);
  const parsed = new Date(year!, month! - 1, day!);
  const sameYear = parsed.getFullYear() === new Date().getFullYear();
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: sameYear ? undefined : "numeric" });
}

function exportTeamCsv(rows: TeamMember[]): void {
  const csv = toCsv(
    ["Name", "Department", "Status", "Streak", "This week", "Blockers this week", "Last reported"],
    rows,
    (m) => [
      m.displayName,
      m.department ?? "",
      STATUS_LABEL[m.status],
      String(m.currentStreak),
      String(m.reportsThisWeek),
      String(m.blockersThisWeek),
      formatLastReported(m.lastReportedDate),
    ],
  );
  downloadCsv(`team-reports-${new Date().toLocaleDateString("en-CA")}.csv`, csv);
}

export function DailyReportTeam() {
  usePageTitle("Team reports");
  const { toast } = useToast();
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [roster, setRoster] = useState<RosterFilter>("required");
  const [department, setDepartment] = useState("");
  // The Excel export covers a date range of its own, not just today's roster.
  const [exportFrom, setExportFrom] = useState(() => new Date(Date.now() - 29 * 86_400_000).toLocaleDateString("en-CA"));
  const [exportTo, setExportTo] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [sort, setSort] = useState<SortState>({ key: "status", dir: "asc" });

  useEffect(() => {
    api
      .get<{ team: TeamMember[] }>("/daily-reports/team")
      .then((res) => setTeam(res.team))
      .catch(() => toast("Could not load your team's reports.", { tone: "danger" }));
  }, [toast]);

  const requiredCount = team?.filter((m) => m.checkInRequired).length ?? 0;
  const inRoster = useMemo(
    () => (team ?? []).filter((m) => roster === "everyone" || m.checkInRequired),
    [team, roster],
  );

  const loading = team === null;
  const missingCount = inRoster.filter((m) => m.status === "missing").length;
  const atRiskCount = inRoster.filter((m) => m.status === "at-risk").length;
  const blockersCount = inRoster.reduce((sum, m) => sum + m.blockersThisWeek, 0);

  const departments = useMemo(
    () => [...new Set(inRoster.map((m) => m.department).filter((d): d is string => Boolean(d)))].sort(),
    [inRoster],
  );

  const filtered = useMemo(() => {
    return inRoster.filter((member) => {
      if (status !== "all" && member.status !== status) return false;
      if (department && member.department !== department) return false;
      if (search.trim() && !member.displayName.toLowerCase().includes(search.trim().toLowerCase())) return false;
      return true;
    });
  }, [inRoster, status, department, search]);

  const sorted = useMemo(() => {
    const direction = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sort.key) {
        case "name":
          return direction * a.displayName.localeCompare(b.displayName);
        case "streak":
          return direction * (a.currentStreak - b.currentStreak);
        case "thisWeek":
          return direction * (a.reportsThisWeek - b.reportsThisWeek);
        case "blockers":
          return direction * (a.blockersThisWeek - b.blockersThisWeek);
        case "lastReported":
          return direction * (a.lastReportedDate ?? "").localeCompare(b.lastReportedDate ?? "");
        case "status":
        default:
          if (STATUS_RANK[a.status] !== STATUS_RANK[b.status]) {
            return direction * (STATUS_RANK[a.status] - STATUS_RANK[b.status]);
          }
          return a.displayName.localeCompare(b.displayName);
      }
    });
  }, [filtered, sort]);

  const isFiltered = status !== "all" || department !== "" || search.trim() !== "";

  return (
    <PageBody
      title="Team reports"
      subtitle="Who's on track, who needs a nudge."
      width="wide"
      actions={
        team && team.length > 0 ? (
          <div className="daily-report-team__export">
            <input type="date" value={exportFrom} max={exportTo} onChange={(e) => setExportFrom(e.target.value)} aria-label="Export from" />
            <input type="date" value={exportTo} min={exportFrom} onChange={(e) => setExportTo(e.target.value)} aria-label="Export to" />
            <ExportMenu
              kind="daily reports"
              excelPath={`/exports/daily-reports.xlsx?from=${exportFrom}&to=${exportTo}`}
              onCsv={() => exportTeamCsv(sorted)}
            />
          </div>
        ) : undefined
      }
    >
      {loading ? (
        <>
          <div className="daily-report-team__stats">
            <StatTileSkeleton />
            <StatTileSkeleton />
            <StatTileSkeleton />
          </div>
        </>
      ) : team.length === 0 ? (
        <EmptyState icon="people" title="Nobody reports to you" description="Reports from your team will show up here." />
      ) : (
        <>
          <div className="daily-report-team__stats">
            <StatTile
              icon="warning"
              value={missingCount}
              label="Missing today"
              tone={missingCount > 0 ? "danger" : "default"}
            />
            <StatTile
              icon="clock"
              value={atRiskCount}
              label="At risk"
              tone={atRiskCount > 0 ? "warn" : "default"}
            />
            <StatTile
              icon="flag"
              value={blockersCount}
              label="Blockers this week"
              tone={blockersCount > 0 ? "danger" : "default"}
            />
          </div>

          <div className="daily-report-team__filters">
            <label className="daily-report-team__search">
              <Icon name="search" size={14} />
              <input
                type="search"
                placeholder="Search by name…"
                value={search}
                aria-label="Search team"
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>

            <SegmentedControl
              aria-label="Filter by status"
              value={status}
              onChange={setStatus}
              options={[
                { value: "all", label: "All" },
                { value: "missing", label: "Missing" },
                { value: "at-risk", label: "At risk" },
                { value: "on-track", label: "On track" },
              ]}
            />

            {departments.length > 0 && (
              <Select aria-label="Filter by department" value={department} onChange={(e) => setDepartment(e.target.value)}>
                <option value="">Every department</option>
                {departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            )}

            {/* Everyone who isn't asked to check in stays out by default —
                switching this on trades "who needs attention" for "the
                whole workspace roster," which is a different question. */}
            <Select
              aria-label="Who's shown"
              value={roster}
              onChange={(e) => setRoster(e.target.value as RosterFilter)}
              className="daily-report-team__roster"
            >
              <option value="required">Required to check in ({requiredCount})</option>
              <option value="everyone">Everyone ({team.length})</option>
            </Select>
          </div>

          <DataTable
            caption="Your team's daily reports"
            density="compact"
            rows={sorted}
            rowKey={(m) => m.id}
            sort={sort}
            onSortChange={setSort}
            empty={
              <EmptyState
                icon="search"
                title="Nothing matches"
                description={isFiltered ? "Try a different search or filter." : undefined}
              />
            }
            columns={[
              {
                key: "name",
                header: "Person",
                sortable: true,
                rowHeader: true,
                render: (m) => (
                  <Link to={`/daily-report/team/${m.id}`} className="daily-report-team__person-link">
                    <Avatar name={m.displayName} size={24} src={m.avatarUrl ?? undefined} />
                    <span>{m.displayName}</span>
                  </Link>
                ),
              },
              {
                key: "status",
                header: "Status",
                sortable: true,
                width: "120px",
                render: (m) => <Pill tone={STATUS_TONE[m.status]}>{STATUS_LABEL[m.status]}</Pill>,
              },
              {
                key: "streak",
                header: "Streak",
                sortable: true,
                width: "100px",
                render: (m) =>
                  m.currentStreak > 0 ? (
                    <span className="daily-report-team__streak">
                      <Icon name="repeat" size={13} />
                      {m.currentStreak}d
                    </span>
                  ) : (
                    <span className="daily-report-team__muted">—</span>
                  ),
              },
              {
                key: "thisWeek",
                header: "This week",
                sortable: true,
                width: "110px",
                render: (m) => <span className="daily-report-team__muted">{m.reportsThisWeek} days</span>,
              },
              {
                key: "blockers",
                header: "Blockers",
                sortable: true,
                width: "130px",
                render: (m) =>
                  m.blockersThisWeek > 0 ? (
                    <Pill tone="danger">{m.blockersThisWeek} this week</Pill>
                  ) : (
                    <span className="daily-report-team__muted">—</span>
                  ),
              },
              {
                key: "lastReported",
                header: "Last reported",
                sortable: true,
                width: "130px",
                render: (m) => <span className="daily-report-team__muted">{formatLastReported(m.lastReportedDate)}</span>,
              },
            ]}
          />
        </>
      )}
    </PageBody>
  );
}
